import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  PaymentRequestButtonElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { ArrowLeft, CreditCard, Loader2, Check } from "lucide-react";
import { paymentsApi } from "../lib/api";
import { toast } from "sonner";
import { addBreadcrumb, captureError, captureMessage } from "../lib/sentry";
import { useTheme } from "../context/ThemeContext";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const getElementStyle = (theme) => ({
  base: {
    fontSize: "16px",
    color: theme === "dark" ? "#f5f1e8" : "#1a1a1a",
    iconColor: theme === "dark" ? "#f5f1e8" : "#1a1a1a",
    fontFamily: "inherit",
    "::placeholder": { color: theme === "dark" ? "#aaa7a1" : "#73706b" },
  },
  invalid: { color: theme === "dark" ? "#fb7185" : "#dc2626" },
});

function PaymentForm() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [method, setMethod] = useState("card");
  const [cardholderName, setCardholderName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [applePayAvailable, setApplePayAvailable] = useState(false);

  // Check if Apple Pay / Google Pay is available
  useEffect(() => {
    if (!stripe) return;

    const pr = stripe.paymentRequest({
      country: "US",
      currency: "usd",
      total: { label: "Save payment method", amount: 0 },
      requestPayerName: true,
    });

    pr.canMakePayment().then((result) => {
      if (result) {
        addBreadcrumb("Express wallet payment method available", "payments.setup", {
          applePay: Boolean(result.applePay),
          googlePay: Boolean(result.googlePay),
        });
        setPaymentRequest(pr);
        setApplePayAvailable(true);
      }
    });
  }, [stripe]);

  // Handle Apple Pay token
  useEffect(() => {
    if (!paymentRequest) return;

    const handler = async (ev) => {
      setSaving(true);
      setError(null);
      addBreadcrumb("Express wallet setup started", "payments.setup", { method: "wallet" });
      try {
        const res = await paymentsApi.createSetupIntent();
        const clientSecret = res.data.client_secret;

        const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(
          clientSecret,
          { payment_method: ev.paymentMethod.id }
        );

        if (confirmError) {
          ev.complete("fail");
          captureMessage("Express wallet setup failed", "warning", {
            code: confirmError.code,
            type: confirmError.type,
          });
          setError(confirmError.message);
        } else if (setupIntent.status === "succeeded") {
          if (!setupIntent.payment_method) {
            captureMessage("Express wallet setup succeeded without payment_method", "warning", {
              setupIntentId: setupIntent.id,
              status: setupIntent.status,
            });
          }
          ev.complete("success");
          addBreadcrumb("Express wallet setup succeeded", "payments.setup", { method: "wallet" });
          toast.success("Payment method saved!");
          navigate("/settings");
        } else {
          ev.complete("fail");
          captureMessage("Express wallet setup ended with unexpected status", "warning", {
            setupIntentId: setupIntent.id,
            status: setupIntent.status,
          });
          setError("Setup did not complete. Please try again.");
        }
      } catch (error) {
        ev.complete("fail");
        captureError(error, { action: "save_payment_method_wallet" });
        setError("Something went wrong. Please try again.");
      } finally {
        setSaving(false);
      }
    };

    paymentRequest.on("paymentmethod", handler);
    return () => paymentRequest.off("paymentmethod", handler);
  }, [paymentRequest, stripe, navigate]);

  const handleCardSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSaving(true);
    setError(null);
    addBreadcrumb("Card setup started", "payments.setup", { method: "card" });

    try {
      const res = await paymentsApi.createSetupIntent();
      const clientSecret = res.data.client_secret;

      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardNumberElement),
            billing_details: { name: cardholderName.trim() || undefined },
          },
        }
      );

      if (stripeError) {
        captureMessage("Card setup failed in Stripe confirmation", "warning", {
          code: stripeError.code,
          type: stripeError.type,
        });
        setError(stripeError.message);
      } else if (setupIntent.status === "succeeded") {
        if (!setupIntent.payment_method) {
          captureMessage("Card setup succeeded without payment_method", "warning", {
            setupIntentId: setupIntent.id,
            status: setupIntent.status,
          });
        }
        addBreadcrumb("Card setup succeeded", "payments.setup", { method: "card" });
        toast.success("Payment method saved!");
        navigate("/settings");
      } else {
        captureMessage("Card setup ended with unexpected status", "warning", {
          setupIntentId: setupIntent.id,
          status: setupIntent.status,
        });
        setError("Setup did not complete. Please try again.");
      }
    } catch (error) {
      captureError(error, { action: "save_payment_method_card" });
      setError("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="fixed inset-0 paper-texture pointer-events-none" />

      {/* Header */}
      <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-lg mx-auto flex items-center gap-3 p-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/settings")}
            className="rounded-xl"
            data-testid="add-payment-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold">Add Payment Details</h1>
        </div>
      </header>

      <main className="max-w-lg mx-auto p-4 space-y-6 relative z-10">
        {/* Method Selection */}
        <div className="space-y-3" data-testid="payment-method-selector">
          <Label className="text-sm font-medium text-muted-foreground">
            Payment method
          </Label>

          {/* Credit Card option */}
          <label
            className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
              method === "card"
                ? "border-primary bg-primary/5 ring-1 ring-primary"
                : "border-border hover:border-muted-foreground/30"
            }`}
            data-testid="method-card-radio"
          >
            <input
              type="radio"
              name="payment-method"
              value="card"
              checked={method === "card"}
              onChange={() => setMethod("card")}
              className="accent-primary w-4 h-4"
            />
            <CreditCard className="w-5 h-5 text-muted-foreground" />
            <span className="font-medium">Credit or Debit Card</span>
          </label>

          {/* Apple Pay option */}
          <label
            className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
              method === "apple_pay"
                ? "border-primary bg-primary/5 ring-1 ring-primary"
                : "border-border hover:border-muted-foreground/30"
            }`}
            data-testid="method-applepay-radio"
          >
            <input
              type="radio"
              name="payment-method"
              value="apple_pay"
              checked={method === "apple_pay"}
              onChange={() => setMethod("apple_pay")}
              className="accent-primary w-4 h-4"
            />
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
            </svg>
            <span className="font-medium">Apple Pay</span>
          </label>
        </div>

        {/* Card Form */}
        {method === "card" && (
          <form onSubmit={handleCardSubmit} className="space-y-4" data-testid="card-details-form">
            <div className="space-y-2">
              <Label htmlFor="cardholder-name" className="text-sm font-medium">
                Cardholder Name
              </Label>
              <input
                id="cardholder-name"
                type="text"
                value={cardholderName}
                onChange={(e) => setCardholderName(e.target.value)}
                placeholder="Name on card"
                className="w-full h-12 px-3 border border-border rounded-xl bg-background text-base outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                data-testid="cardholder-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Card Number
              </Label>
              <div className="stripe-field">
                <CardNumberElement
                  options={{ style: getElementStyle(theme), showIcon: true }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Expiry
                </Label>
                <div className="stripe-field">
                  <CardExpiryElement
                    options={{ style: getElementStyle(theme) }}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  CVC
                </Label>
                <div className="stripe-field">
                  <CardCvcElement
                    options={{ style: getElementStyle(theme) }}
                  />
                </div>
              </div>
            </div>

            {error && (
              <p className="text-sm text-destructive" data-testid="card-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              className="w-full h-12 rounded-xl text-base font-medium"
              disabled={saving || !stripe}
              data-testid="card-save-btn"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                <><Check className="w-4 h-4 mr-2" /> Save Card</>
              )}
            </Button>
          </form>
        )}

        {/* Apple Pay */}
        {method === "apple_pay" && (
          <div className="space-y-4" data-testid="apple-pay-section">
            {applePayAvailable && paymentRequest ? (
              <>
                <p className="text-sm text-muted-foreground">
                  Tap the button below to save your payment method via Apple Pay.
                </p>
                <PaymentRequestButtonElement
                  options={{
                    paymentRequest,
                    style: {
                      paymentRequestButton: {
                        type: "default",
                        theme: "dark",
                        height: "48px",
                      },
                    },
                  }}
                />
                {error && (
                  <p className="text-sm text-destructive" data-testid="applepay-error">
                    {error}
                  </p>
                )}
              </>
            ) : (
              <div className="p-6 rounded-xl border border-border bg-muted/30 text-center space-y-2">
                <svg className="w-10 h-10 mx-auto text-muted-foreground" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                </svg>
                <p className="text-sm font-medium text-foreground">
                  Apple Pay is not available
                </p>
                <p className="text-xs text-muted-foreground">
                  Apple Pay requires Safari on an Apple device with a card in your Wallet.
                  Please use the Credit Card option instead.
                </p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default function AddPaymentPage() {
  const { theme } = useTheme();
  return (
    <Elements
      key={theme}
      stripe={stripePromise}
      options={{ appearance: { theme: theme === "dark" ? "night" : "stripe" } }}
    >
      <style>{`
        .stripe-field {
          border: 1px solid hsl(var(--border));
          border-radius: 0.75rem;
          background: hsl(var(--background));
          padding: 14px 12px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .stripe-field:focus-within {
          border-color: hsl(var(--primary));
          box-shadow: 0 0 0 2px hsl(var(--primary) / 0.15);
        }
        .stripe-field .StripeElement {
          width: 100%;
        }
      `}</style>
      <PaymentForm />
    </Elements>
  );
}
